import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  useColorModeValue,
  useColorMode,
  Text,
  HStack,
  VStack,
  Badge,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import ProfileModal from '../common/ProfileModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faUser,
  faCog,
  faSignOutAlt,
  faBars,
  faMoon,
  faSun,
  faSearch,
  faChevronDown,
  faGlobe,
  faBuilding,
  faBullhorn,
  faImage,
  faLayerGroup,
  faLightbulb,
  faTriangleExclamation,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { useRecoilState, useRecoilValue } from 'recoil';
import { selectedSiteAtom, sitesWithAllSelector } from '../../state/sitesAtom';
import { userAtom } from '../../state/userAtom';
import { insightsCountAtom } from '../../state/insightsAtom';
import { insightsApi } from '../../api';
import { formatNumber } from '../../utils/format';

const NOTIF_ICON = {
  critical: faTriangleExclamation,
  opportunity: faLightbulb,
  info: faCircleInfo,
};
const NOTIF_COLOR = { critical: 'red.500', opportunity: 'brand.500', info: 'gray.500' };

function formatVisitors(num) {
  if (!num) return '';
  return formatNumber(num);
}

function Header({ onOpenSidebar }) {
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const insightCount = useRecoilValue(insightsCountAtom);
  const unreadCount = insightCount.active;
  const [notifs, setNotifs] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  const bgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.8)',
    'rgba(26, 32, 44, 0.8)'
  );
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const selectBg = useColorModeValue('white', 'gray.700');
  const inputBg = useColorModeValue('gray.100', 'gray.700');
  const menuItemActiveBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const searchDropdownBg = useColorModeValue('white', 'gray.800');
  const [selectedSite, setSelectedSite] = useRecoilState(selectedSiteAtom);
  const sitesFromApi = useRecoilValue(sitesWithAllSelector);
  const user = useRecoilValue(userAtom);

  // Load the top active insights into the notifications dropdown when it opens.
  const loadNotifications = async () => {
    setNotifsLoading(true);
    try {
      const res = await insightsApi.list({ status: 'active', site: selectedSite });
      setNotifs((res.data || []).slice(0, 6));
    } catch (err) {
      setNotifs([]);
    } finally {
      setNotifsLoading(false);
    }
  };

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Search function with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length < 2) {
      setSearchResults(null);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const serverUrl = window.location.origin.replace(':3058', ':3059');
        const token = localStorage.getItem('wr_token');
        const response = await fetch(`${serverUrl}/api/sites/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        const data = await response.json();
        setSearchResults(data.data);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults(null);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        setIsSearching(false);
      }
    };
  }, [searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (type, id) => {
    setShowResults(false);
    setSearchQuery('');
    if (type === 'site') navigate('/sites');
    else if (type === 'advertiser') navigate('/advertisers');
    else if (type === 'campaign') navigate('/campaigns');
    else if (type === 'ad') navigate('/ads');
    else if (type === 'zone') navigate('/zones');
  };

  const totalResults = searchResults
    ? (searchResults.sites?.length || 0) + (searchResults.advertisers?.length || 0) + searchResults.campaigns.length + searchResults.ads.length + searchResults.zones.length
    : 0;

  // Map sites to expected format
  const sites = sitesFromApi.map(s => ({
    id: s.site_id === 'all' ? 'all' : s.site_id.toString(),
    name: s.site_name,
    visitors: formatVisitors(s.weekly_visitors),
  }));

  return (
    <Box
      as="header"
      bg={bgColor}
      backdropFilter="blur(20px)"
      borderBottom="1px"
      borderColor={borderColor}
      px={{ base: 4, md: 6 }}
      py={3}
      position="sticky"
      top={0}
      zIndex={10}
      transition="all 0.2s"
    >
      <Flex justify="space-between" align="center" gap={4}>
        {/* Left side - Mobile menu + Site Selector */}
        <HStack spacing={3} flex={{ base: 1, md: 'none' }}>
          {/* Mobile Menu Button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            icon={<FontAwesomeIcon icon={faBars} />}
            variant="ghost"
            aria-label="Open menu"
            onClick={onOpenSidebar}
            size="sm"
            borderRadius="lg"
          />

          {/* Site Selector */}
          <Menu>
            <MenuButton
              as={Box}
              cursor="pointer"
              bg={selectBg}
              borderRadius="xl"
              px={4}
              py={2}
              border="1px solid"
              borderColor={borderColor}
              _hover={{ borderColor: 'brand.300' }}
              transition="all 0.2s"
              minW={{ base: 'auto', md: '200px' }}
            >
              <Flex align="center" gap={2}>
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="green.400"
                  boxShadow="0 0 0 2px rgba(72, 187, 120, 0.2)"
                />
                <Box flex={1} display={{ base: 'none', sm: 'block' }}>
                  <Text fontSize="sm" fontWeight="600" color={textColor} noOfLines={1}>
                    {sites.find(s => s.id === selectedSite)?.name || 'All Sites'}
                  </Text>
                </Box>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', color: 'gray' }} />
              </Flex>
            </MenuButton>
            <MenuList minW="220px" py={2} overflow="hidden" borderRadius="xl">
              {sites.map((site) => (
                <MenuItem
                  key={site.id}
                  onClick={() => setSelectedSite(site.id)}
                  bg={selectedSite === site.id ? menuItemActiveBg : 'transparent'}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  <Flex justify="space-between" w="100%" align="center">
                    <HStack spacing={2}>
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={site.id === 'all' ? 'brand.400' : 'green.400'}
                      />
                      <Text fontSize="sm" fontWeight={selectedSite === site.id ? '600' : '400'}>
                        {site.name}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color={textSecondary}>
                      {site.visitors}
                    </Text>
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </HStack>

        {/* Center - Search (Desktop only) */}
        <Box position="relative" w="320px" maxW="400px" flex="1" display={{ base: 'none', lg: 'block' }} ref={searchRef}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              {isSearching ? (
                <Spinner size="sm" color="gray.400" />
              ) : (
                <FontAwesomeIcon icon={faSearch} style={{ color: 'gray', fontSize: '14px' }} />
              )}
            </InputLeftElement>
            <Input
              placeholder="Search sites, advertisers, campaigns..."
              size="md"
              bg={inputBg}
              border="none"
              borderRadius="xl"
              _placeholder={{ color: textSecondary }}
              _focus={{ bg: inputBg, boxShadow: '0 0 0 2px var(--chakra-colors-brand-200)' }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults && setShowResults(true)}
            />
          </InputGroup>

          {/* Search Results Dropdown */}
          {showResults && searchResults && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              mt={2}
              bg={searchDropdownBg}
              borderRadius="xl"
              boxShadow="xl"
              border="1px solid"
              borderColor={borderColor}
              zIndex={100}
              maxH="400px"
              overflowY="auto"
            >
              {totalResults === 0 ? (
                <Box p={4} textAlign="center">
                  <Text color={textSecondary} fontSize="sm">No results found</Text>
                </Box>
              ) : (
                <VStack align="stretch" spacing={0} p={2}>
                  {/* Sites */}
                  {searchResults.sites?.length > 0 && (
                    <>
                      <Text fontSize="xs" fontWeight="600" color={textSecondary} px={3} py={2}>
                        SITES
                      </Text>
                      {searchResults.sites.map((item) => (
                        <Box
                          key={`site-${item.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="lg"
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleResultClick('site', item.id)}
                        >
                          <HStack spacing={3}>
                            <Box color="teal.500">
                              <FontAwesomeIcon icon={faGlobe} />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="500">{item.name}</Text>
                              {item.url && (
                                <Text fontSize="xs" color={textSecondary}>{item.url}</Text>
                              )}
                            </Box>
                            <Badge size="sm" colorScheme={item.status === 'active' ? 'green' : 'gray'}>
                              {item.status}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                    </>
                  )}

                  {/* Advertisers */}
                  {searchResults.advertisers?.length > 0 && (
                    <>
                      <Text fontSize="xs" fontWeight="600" color={textSecondary} px={3} py={2} mt={searchResults.sites?.length > 0 ? 2 : 0}>
                        ADVERTISERS
                      </Text>
                      {searchResults.advertisers.map((item) => (
                        <Box
                          key={`advertiser-${item.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="lg"
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleResultClick('advertiser', item.id)}
                        >
                          <HStack spacing={3}>
                            <Box color="orange.500">
                              <FontAwesomeIcon icon={faBuilding} />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="500">{item.name}</Text>
                              {item.email && (
                                <Text fontSize="xs" color={textSecondary}>{item.email}</Text>
                              )}
                            </Box>
                            <Badge size="sm" colorScheme={item.status === 'active' ? 'green' : 'gray'}>
                              {item.status}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                    </>
                  )}

                  {/* Campaigns */}
                  {searchResults.campaigns.length > 0 && (
                    <>
                      <Text fontSize="xs" fontWeight="600" color={textSecondary} px={3} py={2} mt={(searchResults.sites?.length > 0 || searchResults.advertisers?.length > 0) ? 2 : 0}>
                        CAMPAIGNS
                      </Text>
                      {searchResults.campaigns.map((item) => (
                        <Box
                          key={`campaign-${item.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="lg"
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleResultClick('campaign', item.id)}
                        >
                          <HStack spacing={3}>
                            <Box color="purple.500">
                              <FontAwesomeIcon icon={faBullhorn} />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="500">{item.name}</Text>
                            </Box>
                            <Badge size="sm" colorScheme={item.status === 'active' ? 'green' : 'gray'}>
                              {item.status}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                    </>
                  )}

                  {/* Ads */}
                  {searchResults.ads.length > 0 && (
                    <>
                      <Text fontSize="xs" fontWeight="600" color={textSecondary} px={3} py={2} mt={2}>
                        ADS
                      </Text>
                      {searchResults.ads.map((item) => (
                        <Box
                          key={`ad-${item.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="lg"
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleResultClick('ad', item.id)}
                        >
                          <HStack spacing={3}>
                            <Box color="blue.500">
                              <FontAwesomeIcon icon={faImage} />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="500">{item.name}</Text>
                              {item.campaign && (
                                <Text fontSize="xs" color={textSecondary}>{item.campaign}</Text>
                              )}
                            </Box>
                            <Badge size="sm" colorScheme={item.status === 'active' ? 'green' : 'gray'}>
                              {item.status}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                    </>
                  )}

                  {/* Zones */}
                  {searchResults.zones.length > 0 && (
                    <>
                      <Text fontSize="xs" fontWeight="600" color={textSecondary} px={3} py={2} mt={2}>
                        ZONES
                      </Text>
                      {searchResults.zones.map((item) => (
                        <Box
                          key={`zone-${item.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="lg"
                          _hover={{ bg: hoverBg }}
                          onClick={() => handleResultClick('zone', item.id)}
                        >
                          <HStack spacing={3}>
                            <Box color="cyan.500">
                              <FontAwesomeIcon icon={faLayerGroup} />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="500">{item.name}</Text>
                              <Text fontSize="xs" color={textSecondary}>
                                {item.size && `${item.size}`}
                                {item.size && item.site && ' · '}
                                {item.site}
                              </Text>
                            </Box>
                            <Badge size="sm" colorScheme={item.status === 'active' ? 'green' : 'gray'}>
                              {item.status}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                    </>
                  )}
                </VStack>
              )}
            </Box>
          )}
        </Box>

        {/* Right side actions */}
        <HStack spacing={{ base: 1, md: 2 }}>
          {/* Dark Mode Toggle */}
          <Tooltip label={colorMode === 'light' ? 'Dark mode' : 'Light mode'} hasArrow>
            <IconButton
              icon={<FontAwesomeIcon icon={colorMode === 'light' ? faMoon : faSun} />}
              variant="ghost"
              aria-label="Toggle color mode"
              size="sm"
              borderRadius="lg"
              onClick={toggleColorMode}
              color={textSecondary}
              _hover={{
                bg: hoverBg,
                color: colorMode === 'light' ? 'brand.500' : 'yellow.400',
              }}
            />
          </Tooltip>

          {/* Notifications */}
          <Menu onOpen={loadNotifications}>
            <MenuButton
              as={IconButton}
              icon={
                <Box position="relative">
                  <FontAwesomeIcon icon={faBell} />
                  {unreadCount > 0 && (
                    <Badge
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      colorScheme="red"
                      variant="solid"
                      borderRadius="full"
                      w="18px"
                      h="18px"
                      fontSize="10px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      p={0}
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Box>
              }
              variant="ghost"
              aria-label="Notifications"
              size="sm"
              borderRadius="lg"
              color={textSecondary}
              _hover={{ bg: hoverBg }}
            />
            <MenuList minW="340px" maxW="340px" py={3}>
              <Flex px={4} pb={2} justify="space-between" align="center">
                <Text fontWeight="600" fontSize="sm">Notifications</Text>
                {unreadCount > 0 && (
                  <Badge colorScheme="red" variant="subtle" borderRadius="full" px={2}>
                    {unreadCount} new
                  </Badge>
                )}
              </Flex>
              <MenuDivider />
              {notifsLoading ? (
                <Box px={4} py={6} textAlign="center">
                  <Spinner size="sm" color="brand.500" />
                </Box>
              ) : notifs.length === 0 ? (
                <Box px={4} py={6} textAlign="center">
                  <Text fontSize="sm" color={textSecondary}>You're all caught up</Text>
                </Box>
              ) : (
                notifs.map((n) => (
                  <MenuItem
                    key={n.insight_id}
                    onClick={() => navigate(n.deep_link || '/insights')}
                    py={2.5}
                  >
                    <HStack spacing={3} align="flex-start" w="100%">
                      <Icon color={NOTIF_COLOR[n.severity] || 'gray.500'} boxSize={3.5} mt={0.5}>
                        <FontAwesomeIcon icon={NOTIF_ICON[n.severity] || faCircleInfo} />
                      </Icon>
                      <Text fontSize="sm" noOfLines={2} flex={1}>{n.title}</Text>
                    </HStack>
                  </MenuItem>
                ))
              )}
              <MenuDivider />
              <MenuItem onClick={() => navigate('/insights')} justifyContent="center" fontSize="sm" fontWeight="600" color="brand.500">
                View all insights
              </MenuItem>
            </MenuList>
          </Menu>

          {/* User Menu */}
          <Menu>
            <MenuButton
              as={Box}
              cursor="pointer"
              bg={selectBg}
              borderRadius="xl"
              px={3}
              py={1.5}
              border="1px solid"
              borderColor={borderColor}
              _hover={{ borderColor: 'brand.300' }}
              transition="all 0.2s"
            >
              <Flex align="center" gap={2}>
                <Avatar
                  size="xs"
                  name={user?.name || 'User'}
                  bg="brand.500"
                  color="white"
                  fontWeight="600"
                />
                <Text fontSize="sm" fontWeight="600" color={textColor} display={{ base: 'none', md: 'block' }}>
                  {user?.name || 'User'}
                </Text>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', color: 'gray' }} />
              </Flex>
            </MenuButton>
            <MenuList py={2} minW="200px" overflow="hidden" borderRadius="xl">
              <Box px={4} pb={2}>
                <Text fontWeight="600" fontSize="sm">{user?.name || 'User'}</Text>
                <Text fontSize="xs" color={textSecondary}>{user?.email || ''}</Text>
              </Box>
              <MenuDivider />
              <MenuItem
                icon={<FontAwesomeIcon icon={faUser} />}
                onClick={onProfileOpen}
                mx={2}
                borderRadius="lg"
                w="calc(100% - 16px)"
              >
                Profile
              </MenuItem>
              <MenuItem
                icon={<FontAwesomeIcon icon={faCog} />}
                onClick={() => navigate('/settings')}
                mx={2}
                borderRadius="lg"
                w="calc(100% - 16px)"
              >
                Settings
              </MenuItem>
              <MenuDivider />
              <MenuItem
                icon={<FontAwesomeIcon icon={faSignOutAlt} />}
                color="red.400"
                onClick={() => {
                  localStorage.removeItem('wr_token');
                  window.location.href = '/login';
                }}
                mx={2}
                borderRadius="lg"
                w="calc(100% - 16px)"
              >
                Sign Out
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Profile Modal */}
      <ProfileModal isOpen={isProfileOpen} onClose={onProfileClose} />
    </Box>
  );
}

export default Header;
